import { withRequestDeadline } from '../sync/requestDeadline'
import { FONT_CATALOG_MANIFEST_URL, getFontCatalogFileUrl } from './fontCatalogConfig'
import {
  readCachedManifest,
  readCoreFontBytes,
  writeCachedManifest,
  writeCoreFontBytes,
} from './fontCatalogStorage'
import type {
  FontCatalogFamily,
  FontCatalogFile,
  FontCatalogManifest,
  FontCatalogStyle,
} from '../../types/fontCatalog'

const lazyFaces = new Map<string, FontFace>()
const cachedLazyFaces = new Set<string>()
const registeredFaces = new Set<string>()
const knownFamiliesByName = new Map<string, FontCatalogFamily>()

function familyNameKey(name: string): string {
  return name.trim().toLowerCase()
}

function rememberCatalogFamily(family: FontCatalogFamily): void {
  knownFamiliesByName.set(familyNameKey(family.family), family)
}

function faceKey(file: FontCatalogFile): string {
  return `${file.path}\u0000${file.weight}\u0000${file.style}\u0000${file.unicodeRange}`
}

function isCatalogFile(value: unknown): value is FontCatalogFile {
  if (!value || typeof value !== 'object') return false
  const file = value as Record<string, unknown>
  return typeof file.path === 'string'
    && typeof file.weight === 'string'
    && (file.style === 'normal' || file.style === 'italic')
    && typeof file.unicodeRange === 'string'
}

function parseFontCatalogManifest(value: unknown): FontCatalogManifest {
  if (!value || typeof value !== 'object' || !Array.isArray((value as { fonts?: unknown }).fonts)) {
    throw new Error('Font catalog manifest does not contain a fonts array')
  }

  const fonts = (value as { fonts: unknown[] }).fonts
  for (const valueFamily of fonts) {
    if (!valueFamily || typeof valueFamily !== 'object') {
      throw new Error('Font catalog contains an invalid family entry')
    }
    const family = valueFamily as Record<string, unknown>
    if (
      typeof family.id !== 'string'
      || typeof family.family !== 'string'
      || typeof family.license !== 'string'
      || typeof family.designer !== 'string'
      || typeof family.category !== 'string'
      || !Array.isArray(family.subsets)
      || !family.subsets.every((subset) => typeof subset === 'string')
      || !Array.isArray(family.files)
      || !family.files.every(isCatalogFile)
    ) {
      throw new Error(`Font catalog contains an invalid family entry: ${String(family.id || 'unknown')}`)
    }
  }

  const manifest = value as FontCatalogManifest
  for (const family of manifest.fonts) rememberCatalogFamily(family)
  return manifest
}

/** Returns manifest data already loaded by the catalog or installed-font bootstrap. */
export function findKnownCatalogFamilyByName(name: string): FontCatalogFamily | null {
  return knownFamiliesByName.get(familyNameKey(name)) ?? null
}

/**
 * Revalidates through the browser HTTP cache. Cloudflare Pages supplies an
 * ETag, so `cache: no-cache` produces a conditional request without manually
 * setting If-None-Match (which would trigger a rejected CORS preflight).
 */
async function refreshFontCatalog(): Promise<FontCatalogManifest> {
  const response = await fetch(FONT_CATALOG_MANIFEST_URL, { cache: 'no-cache' })
  if (!response.ok) {
    throw new Error(`Font catalog request failed (${response.status})`)
  }
  const manifest = parseFontCatalogManifest(await response.json())
  await writeCachedManifest(manifest, response.headers.get('etag'))
  return manifest
}

export async function getFontCatalog(): Promise<{
  manifest: FontCatalogManifest
  refresh: Promise<FontCatalogManifest> | null
}> {
  const cached = await readCachedManifest()
  if (cached) {
    try {
      return { manifest: parseFontCatalogManifest(cached), refresh: refreshFontCatalog() }
    } catch {
      // Ignore an obsolete/corrupt local cache and replace it from upstream.
    }
  }
  return { manifest: await refreshFontCatalog(), refresh: null }
}

export function coversBasicLatin(file: FontCatalogFile): boolean {
  // Every usable core face covers ASCII letters. This fallback supports fonts
  // that ship as one whole file and therefore have no "latin" path suffix.
  const ranges = file.unicodeRange.toUpperCase().matchAll(/U\+([0-9A-F]+)(?:-([0-9A-F]+))?/g)
  let coversUpper = false
  let coversLower = false
  for (const match of ranges) {
    const start = Number.parseInt(match[1], 16)
    const end = Number.parseInt(match[2] || match[1], 16)
    coversUpper ||= start <= 0x41 && end >= 0x5a
    coversLower ||= start <= 0x61 && end >= 0x7a
  }
  return coversUpper && coversLower
}

function chooseCoreFile(files: FontCatalogFile[]): FontCatalogFile | null {
  const latin = files.filter((file) => /-latin\.woff2$/i.test(file.path))
  const candidates = latin.length > 0 ? latin : files.filter(coversBasicLatin)
  if (candidates.length === 0) return null

  return candidates.find((file) => file.weight.trim().split(/\s+/).length === 2)
    ?? candidates.find((file) => file.weight.trim() === '400')
    ?? candidates[0]
}

/** Selects the one eagerly loaded core face for each style in a family. */
export function getCoreFontFiles(family: FontCatalogFamily): FontCatalogFile[] {
  const styles = new Set<FontCatalogStyle>(family.files.map((file) => file.style))
  return [...styles].flatMap((style) => {
    const core = chooseCoreFile(family.files.filter((file) => file.style === style))
    return core ? [core] : []
  })
}

function fontFaceDescriptors(file: FontCatalogFile): FontFaceDescriptors {
  return {
    weight: file.weight,
    style: file.style,
    unicodeRange: file.unicodeRange,
  }
}

async function addLazyFace(family: FontCatalogFamily, file: FontCatalogFile): Promise<void> {
  const key = faceKey(file)
  const bytes = await readCoreFontBytes(file.path)
  if (registeredFaces.has(key) && (!bytes || cachedLazyFaces.has(key))) return
  const previous = lazyFaces.get(key)
  if (previous) document.fonts.delete(previous)
  const source = bytes || `url("${getFontCatalogFileUrl(file.path)}")`
  const face = new FontFace(family.family, source, fontFaceDescriptors(file))
  if (bytes) await face.load()
  document.fonts.add(face)
  lazyFaces.set(key, face)
  if (bytes) cachedLazyFaces.add(key)
  registeredFaces.add(key)
}

async function loadCoreFace(
  family: FontCatalogFamily,
  file: FontCatalogFile,
  fetchMissing: boolean,
): Promise<void> {
  const key = faceKey(file)
  if (registeredFaces.has(key)) return

  const bytes = await readCatalogFontBytes(family, file, fetchMissing)
  if (!bytes) {
    throw new Error(`The local core file for ${family.family} is missing`)
  }

  const face = new FontFace(family.family, bytes, fontFaceDescriptors(file))
  await face.load()
  document.fonts.add(face)
  registeredFaces.add(key)
}

/** Reads a catalog file from the existing font cache, fetching it only when allowed. */
export async function readCatalogFontBytes(
  family: FontCatalogFamily,
  file: FontCatalogFile,
  fetchMissing: boolean,
  signal?: AbortSignal,
): Promise<ArrayBuffer | null> {
  let bytes = await readCoreFontBytes(file.path)
  if (!bytes && fetchMissing) {
    bytes = await withRequestDeadline(async bounded => {
      const response = await fetch(getFontCatalogFileUrl(file.path), { signal: bounded })
      if (!response.ok) throw new Error(`Failed to download ${family.family} (${response.status})`)
      return response.arrayBuffer()
    }, { signal })
    if (!bytes.byteLength) throw new Error('Empty font file')
  }
  // Also promote readable native legacy files during an explicit download or
  // installation. Unchanged primary cache entries avoid a redundant write.
  if (bytes && fetchMissing) await writeCoreFontBytes(file.path, bytes)
  return bytes
}

export async function registerCatalogFamily(
  family: FontCatalogFamily,
  options: { fetchMissingCore: boolean },
): Promise<void> {
  rememberCatalogFamily(family)
  if (typeof document === 'undefined' || typeof FontFace === 'undefined') {
    throw new Error('This browser does not support the CSS Font Loading API')
  }

  const coreFiles = getCoreFontFiles(family)
  if (coreFiles.length === 0) {
    throw new Error(`${family.family} has no file covering basic Latin`)
  }
  await Promise.all(coreFiles.map((file) => loadCoreFace(family, file, options.fetchMissingCore)))
  for (const file of family.files) {
    if (!coreFiles.includes(file)) await addLazyFace(family, file)
  }
}
